import { memo } from "react";
import { useTranslations } from "next-intl";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import { useCopyToClipboard } from "./useCopyToClipboard";

/**
 * Fenced code block for assistant messages. Mono text, horizontal scroll, an
 * optional language label and a copy button. Rendered as a React NODE by
 * renderMarkdownLite — never innerHTML — so model output stays inert (§1b).
 */
export const CodeBlock = memo(function CodeBlock({
  code,
  lang,
}: {
  code: string;
  lang?: string;
}) {
  const t = useTranslations("aiAssistant");
  const { copied, copy } = useCopyToClipboard();
  return (
    <div className="my-1.5 rounded-lg border outline-quaternary surface-primary overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-2.5 py-1 surface-secondary border-b outline-quaternary">
        <span className="min-w-0 truncate text-[11px] font-mono font-tertiary">
          {lang || t("codeBlock")}
        </span>
        <HoverInfo message={copied ? t("copied") : t("copyCode")}>
          <button
            type="button"
            data-testid="ai-code-copy"
            aria-label={t("copyCode")}
            className="flex shrink-0 items-center gap-1 rounded-md p-1 text-[11px] font-tertiary hover:surface-tertiary transition-colors cursor-pointer focus-ring"
            onClick={() => copy(code)}
          >
            {copied ? <IconCheck size={13} /> : <IconCopy size={13} />}
          </button>
        </HoverInfo>
      </div>
      <pre className="overflow-x-auto px-3 py-2 text-xs font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
});
